import { useEffect, useRef, useState } from "react";
import { Coordinates, Qibla } from "adhan";
import { useLang } from "@/context/LangContext";
import { logWarn } from "@/lib/log";
import { fill } from "@/lib/utils";
import { Compass, Navigation2, Volume2, VolumeX, CheckCircle2, RotateCcw, RotateCw, AlertTriangle, Vibrate, VibrateOff, Smartphone, X } from "lucide-react";

const KAABA = { lat: 21.4225, lng: 39.8262 };
const ALIGN_DEG = 5;      // needle counts as "on the Qibla" within ±5°
const REARM_DEG = 10;     // …and stays locked until it drifts beyond ±10° (hysteresis)
const NEAR_DEG = 20;      // amber "almost there" zone
const SENSOR_ALPHA = 0.25; // low-pass on raw sensor readings (0..1, lower = smoother)
const NEEDLE_EASE = 0.07;  // per-frame easing toward the filtered heading (lower = slower needle)
const HAPTIC_FAR_MS = 1500, HAPTIC_NEAR_MS = 150, HAPTIC_RANGE_DEG = 90;
const HAPTIC_KEY = "qibla_haptic";
const SENSOR_TIMEOUT_MS = 3000;

// Spoken announcement when the phone locks onto the Qibla (Web Speech API, offline).
const ANNOUNCE = {
  id: "Kiblat ditemukan. Anda kini menghadap kiblat.",
  en: "Qibla found. You are now facing the Qibla.",
  ar: "تم تحديد القبلة. أنت الآن تتجه نحو القبلة.",
  fr: "Qibla trouvée. Vous faites maintenant face à la Qibla.",
  de: "Qibla gefunden. Sie sind jetzt zur Qibla ausgerichtet.",
  es: "Alcanzaste la Qibla. Ahora estás orientado hacia la Qibla.",
  ru: "Кибла найдена. Теперь вы обращены к Кибле.",
  zh: "已找到朝向。您现在正对着朝向。",
  ja: "キブラが見つかりました。今、キブラの方向を向いています。",
  ko: "키블라를 찾았습니다. 이제 키블라를 향하고 있습니다.",
  hi: "क़िबला मिल गया। अब आप क़िबला की ओर हैं।",
};
const SPEECH_LANG = { id: "id-ID", en: "en-US", ar: "ar-SA", fr: "fr-FR", de: "de-DE", es: "es-ES", ru: "ru-RU", zh: "zh-CN", ja: "ja-JP", ko: "ko-KR", hi: "hi-IN" };

const norm360 = (a) => ((a % 360) + 360) % 360;
const signed180 = (a) => ((a + 540) % 360) - 180; // → (-180, 180]
const screenAngle = () => { try { return window.screen?.orientation?.angle ?? window.orientation ?? 0; } catch { return 0; } };
const readHaptic = () => { try { return localStorage.getItem(HAPTIC_KEY) !== "0"; } catch { return true; } };
const vibrate = (pattern) => { try { return !!navigator.vibrate?.(pattern); } catch (e) { logWarn("vibrate unsupported:", e); return false; } };
// Pulse cadence: far (≥90°) every 1.5 s → almost aligned every 0.15 s. Left = 1 pulse, right = 2 pulses.
const hapticPlan = (off, offset) => ({
  interval: Math.round(HAPTIC_NEAR_MS + (Math.min(off, HAPTIC_RANGE_DEG) / HAPTIC_RANGE_DEG) * (HAPTIC_FAR_MS - HAPTIC_NEAR_MS)),
  pattern: offset > 0 ? [40, 90, 40] : [40],
  pulses: offset > 0 ? 2 : 1,
});

// Heading from a device-orientation event, compensated for screen rotation; null when unusable.
function eventHeading(e) {
  if (e.webkitCompassHeading != null && e.webkitCompassHeading >= 0) return norm360(e.webkitCompassHeading + screenAngle());
  if (e.alpha == null) return null;
  return norm360(360 - e.alpha + screenAngle());
}

// Heading (0 = top, clockwise) of a pointer position relative to the dial centre.
function pointerHeading(el, ev) {
  const r = el.getBoundingClientRect();
  return norm360((Math.atan2(ev.clientY - (r.top + r.height / 2), ev.clientX - (r.left + r.width / 2)) * 180) / Math.PI + 90);
}

export default function LiveQiblaCompass() {
  const { tc, lang } = useLang();
  const [coords, setCoords] = useState(null);
  const [heading, setHeading] = useState(null);   // smoothed heading shown by the needle
  const [mode, setMode] = useState(null);         // null | "sensor" | "sim"
  const [simHeading, setSimHeading] = useState(0);
  const [sensorMissing, setSensorMissing] = useState(false);
  const [aligned, setAligned] = useState(false);
  const [lowAccuracy, setLowAccuracy] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [hapticOn, setHapticOn] = useState(readHaptic);
  const [hapticState, setHapticState] = useState(null); // {interval, pulses} for the on-screen indicator
  const orientRef = useRef(null);
  const targetRef = useRef(null);   // filtered heading the needle eases toward (sensor or simulated)
  const smoothRef = useRef(null);   // eased needle heading
  const absoluteRef = useRef(false); // once absolute events arrive, ignore relative ones
  const rafRef = useRef(0);
  const dialRef = useRef(null);
  const dragRef = useRef(false);
  const announcedRef = useRef(false);
  const voiceOnRef = useRef(true);
  const guideRef = useRef({ off: null, offset: null, aligned: false });
  useEffect(() => { voiceOnRef.current = voiceOn; }, [voiceOn]);
  const enabled = mode != null;

  useEffect(() => {
    if (!navigator.geolocation) { setCoords(KAABA); return; }
    const id = navigator.geolocation.watchPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setCoords(KAABA),
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  const qiblaBearing = coords ? Qibla(new Coordinates(coords.lat, coords.lng)) : 0;
  const needleAngle = enabled && heading != null ? norm360(qiblaBearing - heading) : qiblaBearing;
  const offset = enabled && heading != null ? signed180(needleAngle) : null; // + → turn right, − → turn left
  const off = offset == null ? null : Math.abs(offset);
  const near = off != null && !aligned && off <= NEAR_DEG;
  guideRef.current = { off, offset, aligned };

  // Spoken announcement the moment the phone locks onto the Qibla.
  const speakAligned = () => {
    if (!voiceOnRef.current) return;
    try {
      const synth = window.speechSynthesis;
      if (!synth) return;
      synth.cancel();
      const u = new SpeechSynthesisUtterance(ANNOUNCE[lang] || ANNOUNCE.en);
      u.lang = SPEECH_LANG[lang] || "en-US";
      u.rate = 0.95;
      synth.speak(u);
    } catch (e) { logWarn("Qibla voice announce failed:", e); }
  };

  useEffect(() => {
    if (off == null) { setAligned(false); announcedRef.current = false; return; }
    if (off <= ALIGN_DEG) {
      setAligned(true);
      if (!announcedRef.current) { announcedRef.current = true; speakAligned(); if (hapticOn) vibrate([320]); }
    } else if (off > REARM_DEG) {
      setAligned(false);
      announcedRef.current = false; // re-arm once the user drifts away
    }
  }, [off, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  // Haptic guidance: pulses get faster as the phone approaches the Qibla; stops while aligned.
  useEffect(() => {
    if (!enabled || !hapticOn) { setHapticState(null); return; }
    let t, cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const g = guideRef.current;
      let wait = HAPTIC_FAR_MS;
      if (g.off != null && !g.aligned) {
        const plan = hapticPlan(g.off, g.offset);
        vibrate(plan.pattern);
        setHapticState({ interval: plan.interval, pulses: plan.pulses });
        wait = plan.interval;
      } else setHapticState(g.aligned ? { interval: 0, pulses: 0 } : null);
      t = setTimeout(tick, wait);
    };
    t = setTimeout(tick, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [enabled, hapticOn]);

  // Needle animation: ease the displayed heading toward the target each frame along the shortest arc,
  // so the needle moves slowly and never spins the long way around.
  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      const target = targetRef.current;
      if (target != null) {
        if (smoothRef.current == null) smoothRef.current = target;
        const d = signed180(target - smoothRef.current);
        smoothRef.current = Math.abs(d) < 0.05 ? target : norm360(smoothRef.current + d * NEEDLE_EASE);
        setHeading((h) => (h != null && Math.abs(signed180(smoothRef.current - h)) < 0.05 ? h : smoothRef.current));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [enabled]);

  // Suggest simulation when no orientation event arrives shortly after enabling the sensor.
  useEffect(() => {
    if (mode !== "sensor") return;
    const t = setTimeout(() => { if (targetRef.current == null) setSensorMissing(true); }, SENSOR_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [mode]);

  const removeSensor = () => {
    if (orientRef.current) {
      window.removeEventListener("deviceorientationabsolute", orientRef.current, true);
      window.removeEventListener("deviceorientation", orientRef.current, true);
      orientRef.current = null;
    }
  };

  const enableCompass = async () => {
    const handler = (e) => {
      const isAbsolute = e.type === "deviceorientationabsolute" || e.absolute === true || e.webkitCompassHeading != null;
      if (isAbsolute) absoluteRef.current = true;
      else if (absoluteRef.current) return; // prefer the true-north source when both fire
      const h = eventHeading(e);
      if (h == null) return;
      setSensorMissing(false);
      if (e.webkitCompassAccuracy != null) setLowAccuracy(e.webkitCompassAccuracy < 0 || e.webkitCompassAccuracy > 30);
      else setLowAccuracy(!isAbsolute);
      // Circular low-pass filter (sin/cos average) removes sensor jitter without wrap-around glitches.
      const prev = targetRef.current;
      if (prev == null) { targetRef.current = h; return; }
      const a = SENSOR_ALPHA;
      const x = (1 - a) * Math.cos((prev * Math.PI) / 180) + a * Math.cos((h * Math.PI) / 180);
      const y = (1 - a) * Math.sin((prev * Math.PI) / 180) + a * Math.sin((h * Math.PI) / 180);
      targetRef.current = norm360((Math.atan2(y, x) * 180) / Math.PI);
    };
    try {
      if (typeof DeviceOrientationEvent === "undefined") { setSensorMissing(true); return; }
      if (DeviceOrientationEvent.requestPermission) {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res !== "granted") { setSensorMissing(true); return; }
      }
      window.addEventListener("deviceorientationabsolute", handler, true);
      window.addEventListener("deviceorientation", handler, true);
      orientRef.current = handler;
      setMode("sensor");
    } catch (e) { logWarn("Device orientation/compass not available:", e); setSensorMissing(true); }
  };

  const setSim = (h) => { const v = norm360(Math.round(h)); setSimHeading(v); targetRef.current = v; };
  const startSim = () => { removeSensor(); setSensorMissing(false); setLowAccuracy(false); absoluteRef.current = false; setSim(smoothRef.current ?? 0); setMode("sim"); };
  const stopSim = () => { setMode(null); setHeading(null); targetRef.current = null; smoothRef.current = null; };

  const onDialPointerDown = (e) => { if (mode !== "sim") return; dragRef.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); setSim(pointerHeading(e.currentTarget, e)); };
  const onDialPointerMove = (e) => { if (mode === "sim" && dragRef.current) setSim(pointerHeading(e.currentTarget, e)); };
  const onDialPointerUp = () => { dragRef.current = false; };

  useEffect(() => () => removeSensor(), []);

  const toggleHaptic = () => { setHapticOn((v) => { const n = !v; try { localStorage.setItem(HAPTIC_KEY, n ? "1" : "0"); } catch (e) { logWarn("haptic pref not saved:", e); } return n; }); };

  const dial = aligned
    ? "border-[hsl(var(--gold))] ring-4 ring-[hsl(var(--gold))]/40 shadow-[0_0_32px_hsl(var(--gold)/0.45)]"
    : near ? "border-amber-400/70 ring-2 ring-amber-400/30" : "border-primary/20";
  const fmtSec = (ms) => (ms / 1000).toLocaleString(SPEECH_LANG[lang] || "en-US", { minimumFractionDigits: 1, maximumFractionDigits: 2 });

  return (
    <div className="flex flex-col items-center py-2" data-testid="live-qibla-compass" data-aligned={aligned || undefined} data-mode={mode || undefined}>
      <div
        ref={dialRef}
        onPointerDown={onDialPointerDown} onPointerMove={onDialPointerMove} onPointerUp={onDialPointerUp} onPointerCancel={onDialPointerUp}
        data-testid="live-qibla-dial"
        className={`relative h-56 w-56 rounded-full border-2 flex items-center justify-center select-none transition-[border-color,box-shadow] duration-500 ${dial} bg-gradient-to-b from-secondary to-card ${mode === "sim" ? "cursor-grab active:cursor-grabbing touch-none" : ""}`}
      >
        {["N", "E", "S", "W"].map((d, i) => (
          <span
            key={d}
            className="absolute text-xs font-semibold text-muted-foreground pointer-events-none"
            style={{ transform: `rotate(${i * 90}deg) translateY(-102px) rotate(${-i * 90}deg)` }}
          >
            {d}
          </span>
        ))}
        {/* Fixed top marker = direction the phone is pointing */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1 h-0 w-0 border-x-[6px] border-x-transparent border-t-[10px] border-t-muted-foreground/60" />
        <div
          className={`absolute h-full w-full will-change-transform pointer-events-none ${enabled ? "" : "transition-transform duration-500 ease-out"}`}
          style={{ transform: `rotate(${needleAngle}deg)` }}
          data-testid="live-qibla-needle"
          data-aligned={aligned || undefined}
          data-angle={Math.round(needleAngle * 10) / 10}
        >
          <div className={`absolute left-1/2 top-3 -translate-x-1/2 flex flex-col items-center transition-transform duration-500 ${aligned ? "scale-110" : ""}`}>
            <img
              src="/kaaba-qibla.png" alt="Ka'bah" draggable={false}
              className={`h-9 w-9 rounded-md shadow transition-[box-shadow] duration-500 ${aligned ? "ring-2 ring-[hsl(var(--gold))] shadow-[0_0_18px_hsl(var(--gold))]" : ""}`}
            />
            <div className={`mt-1 w-0 h-0 border-x-[7px] border-x-transparent border-b-[14px] transition-colors duration-500 ${aligned ? "border-b-[hsl(var(--gold))]" : near ? "border-b-amber-500" : "border-b-primary"}`} />
          </div>
        </div>
        {hapticOn && hapticState && hapticState.interval > 0 && (
          <span
            key={`${hapticState.interval}-${hapticState.pulses}`}
            className="absolute inset-1 rounded-full border-2 border-primary/60 pointer-events-none animate-pulse"
            style={{ animationDuration: `${hapticState.interval}ms` }}
            data-testid="live-qibla-haptic-ring"
          />
        )}
        <div className={`h-3 w-3 rounded-full transition-colors duration-500 ${aligned ? "bg-[hsl(var(--gold))]" : "bg-primary"}`} />
      </div>
      <p className="mt-5 text-3xl font-serif" data-testid="live-qibla-bearing">{Math.round(qiblaBearing)}&deg;</p>

      {aligned ? (
        <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold))] px-4 py-1.5 text-sm font-semibold" data-testid="live-qibla-aligned">
          <CheckCircle2 className="h-4 w-4" />
          {tc({ id: "Menghadap Kiblat ✓", en: "Facing the Qibla ✓", ar: "أنت تتجه نحو القبلة ✓" })}
        </p>
      ) : offset != null && (
        <p className={`mt-2 inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold tabular-nums ${near ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" : "bg-secondary text-muted-foreground"}`} data-testid="live-qibla-hint" data-offset={Math.round(offset)}>
          {offset > 0 ? <RotateCw className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
          {offset > 0
            ? fill(tc({ id: "Putar ke kanan {deg}°", en: "Turn right {deg}°", ar: "استدر يمينًا {deg}°" }), { deg: Math.round(off) })
            : fill(tc({ id: "Putar ke kiri {deg}°", en: "Turn left {deg}°", ar: "استدر يسارًا {deg}°" }), { deg: Math.round(off) })}
        </p>
      )}

      {enabled && hapticOn && hapticState && (
        <p className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground tabular-nums" data-testid="live-qibla-haptic-status" data-interval={hapticState.interval} data-pulses={hapticState.pulses}>
          <Vibrate className="h-3 w-3" />
          {hapticState.interval > 0
            ? fill(hapticState.pulses === 2
              ? tc({ id: "Getaran tiap {sec} dtk · 2 denyut (kanan)", en: "Vibrates every {sec} s · 2 pulses (right)", ar: "اهتزاز كل {sec} ث · نبضتان (يمين)" })
              : tc({ id: "Getaran tiap {sec} dtk · 1 denyut (kiri)", en: "Vibrates every {sec} s · 1 pulse (left)", ar: "اهتزاز كل {sec} ث · نبضة واحدة (يسار)" }), { sec: fmtSec(hapticState.interval) })
            : tc({ id: "Getaran panjang — tepat menghadap kiblat", en: "Long vibration — locked on the Qibla", ar: "اهتزاز طويل — أنت على القبلة تمامًا" })}
        </p>
      )}

      <p className="text-sm text-muted-foreground mt-1 text-center max-w-xs">
        {mode === "sim"
          ? tc({ id: "Mode simulasi — geser slider atau seret dial untuk memutar HP secara virtual", en: "Simulation mode — move the slider or drag the dial to rotate the phone virtually", ar: "وضع المحاكاة — حرّك شريط التمرير أو اسحب القرص لتدوير الهاتف افتراضيًا" })
          : tc({ id: "Arahkan puncak HP ke arah panah untuk menghadap Kiblat", en: "Point the top of your phone toward the arrow to face the Qibla", ar: "وجّه أعلى هاتفك نحو السهم لتستقبل القبلة" })}
      </p>

      {mode === "sim" && (
        <div className="mt-3 w-full max-w-xs" data-testid="live-qibla-sim">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span className="inline-flex items-center gap-1"><Smartphone className="h-3.5 w-3.5" />{tc({ id: "Arah HP", en: "Phone heading", ar: "اتجاه الهاتف" })}</span>
            <span className="font-semibold tabular-nums text-foreground" data-testid="live-qibla-sim-heading">{simHeading}&deg;</span>
          </div>
          <input
            type="range" min={0} max={359} step={1} value={simHeading}
            onChange={(e) => setSim(Number(e.target.value))}
            data-testid="live-qibla-sim-slider"
            aria-label={tc({ id: "Arah HP (simulasi)", en: "Phone heading (simulation)", ar: "اتجاه الهاتف (محاكاة)" })}
            className="w-full accent-[hsl(var(--primary))]"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground"><span>0° N</span><span>90° E</span><span>180° S</span><span>270° W</span><span>359°</span></div>
        </div>
      )}

      {mode === "sensor" && lowAccuracy && !sensorMissing && (
        <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 text-center max-w-xs" data-testid="live-qibla-calibrate">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {tc({ id: "Akurasi kompas rendah — gerakkan HP membentuk angka 8 untuk kalibrasi", en: "Low compass accuracy — move your phone in a figure-8 to calibrate", ar: "دقة البوصلة منخفضة — حرّك هاتفك على شكل الرقم 8 للمعايرة" })}
        </p>
      )}
      {sensorMissing && mode !== "sim" && (
        <div className="mt-3 flex flex-col items-center gap-2 rounded-2xl bg-amber-500/10 px-4 py-3 text-center text-xs text-amber-700 dark:text-amber-300 max-w-xs" data-testid="live-qibla-sensor-missing">
          <span className="inline-flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />{tc({ id: "Sensor kompas tidak tersedia di perangkat ini. Coba Mode Simulasi untuk berlatih.", en: "No compass sensor is available on this device. Try Simulation Mode to practise.", ar: "لا يتوفر مستشعر بوصلة على هذا الجهاز. جرّب وضع المحاكاة للتدريب." })}</span>
          <button onClick={startSim} data-testid="live-qibla-sim-suggest" className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white h-8 px-4 text-xs font-medium hover:opacity-90">
            <Smartphone className="h-3.5 w-3.5" />{tc({ id: "Mulai Mode Simulasi", en: "Start Simulation Mode", ar: "بدء وضع المحاكاة" })}
          </button>
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        {!enabled ? (
          <>
            <button
              onClick={enableCompass}
              data-testid="live-qibla-enable"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-white h-10 px-5 text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Compass className="h-4 w-4" />
              {tc({ id: "Aktifkan Kompas", en: "Enable compass", ar: "تفعيل البوصلة" })}
            </button>
            <button
              onClick={startSim}
              data-testid="live-qibla-sim-start"
              className="inline-flex items-center gap-2 rounded-full border border-border h-10 px-4 text-sm font-medium hover:bg-secondary transition-colors"
            >
              <Smartphone className="h-4 w-4" />
              {tc({ id: "Mode Simulasi", en: "Simulation mode", ar: "وضع المحاكاة" })}
            </button>
          </>
        ) : mode === "sim" ? (
          <button onClick={stopSim} data-testid="live-qibla-sim-stop" className="inline-flex items-center gap-1.5 rounded-full border border-border h-10 px-4 text-xs font-medium text-muted-foreground hover:bg-secondary transition-colors">
            <X className="h-3.5 w-3.5" />{tc({ id: "Keluar simulasi", en: "Exit simulation", ar: "الخروج من المحاكاة" })}
          </button>
        ) : !sensorMissing && (
          <p className="text-xs text-primary inline-flex items-center gap-1">
            <Navigation2 className="h-3.5 w-3.5" />
            {tc({ id: "Kompas aktif — berputar mengikuti HP", en: "Compass active — following your phone", ar: "البوصلة نشطة — تتبع هاتفك" })}
          </p>
        )}
        <button
          onClick={() => { setVoiceOn((v) => !v); try { window.speechSynthesis?.cancel(); } catch (e) { logWarn("speechSynthesis cancel failed:", e); } }}
          data-testid="live-qibla-voice-toggle"
          aria-pressed={voiceOn}
          title={tc({ id: "Pengumuman suara", en: "Voice announcement", ar: "الإعلان الصوتي" })}
          className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${voiceOn ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
        >
          {voiceOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        </button>
        <button
          onClick={toggleHaptic}
          data-testid="live-qibla-haptic-toggle"
          aria-pressed={hapticOn}
          title={tc({ id: "Getaran arah kiblat", en: "Qibla haptic guidance", ar: "الاهتزاز لتوجيه القبلة" })}
          className={`inline-flex h-10 w-10 items-center justify-center rounded-full border transition-colors ${hapticOn ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:bg-secondary"}`}
        >
          {hapticOn ? <Vibrate className="h-4 w-4" /> : <VibrateOff className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}
